<script setup>
import TD from "@/Components/Table/Element.vue";
import Label from "@/Components/Settings/Label.vue";
import Input from "@/Components/Settings/Input.vue";
import Select from "@/Components/Select.vue";
import Button from "@/Components/Button.vue";
import HeaderButton from "@/Components/Settings/HeaderButton.vue";

import { ref, onMounted, watch, computed } from "vue";
import axios from "axios";

const props = defineProps({
  show: Boolean,
  rooms: Array,
  practitioners: Array,
  practitioner_id: String,
  room_id: String,
  duration: Number,
  start: String,
  end: String,
  interval: Number,
  errors: Object,
});

const freeSlots = ref([]);

const practitioner_id = ref(props.practitioner_id);
const room_id = ref(props.room_id);
const duration = ref(props.duration);
const date_start = ref(props.start);
const date_end = ref(props.start);
const date_start_to_show = ref(props.start);
const date_end_to_show = ref(props.start);

const userName = computed(() => {
  if (practitioner_id.value) {
    return props.practitioners.find((p) => p.id == props.practitioner_id)
      .fullname;
  }
  return "";
});

const roomName = computed(() => {
  if (room_id.value) {
    return props.rooms.find((r) => r.id == props.room_id).name;
  }
  return "";
});

const emit = defineEmits(["close", "chooseSlot"]);

const show = ref(props.show);
const noSlotsFound = ref(false);

function searchSlots() {
  noSlotsFound.value = false;
  axios
    .post(
      "/termin-suchen/async",
      {
        start: date_start.value,
        end: date_end.value,
        raum: room_id.value,
        behandler: practitioner_id.value,
        dauer: duration.value,
      },
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    )
    .then((res) => {
      date_start_to_show.value = date_start.value;
      date_end_to_show.value = date_end.value;
      freeSlots.value = res.data;
      if (freeSlots.value.length === 0) {
        noSlotsFound.value = true;
      }
    })
    .catch((err) => {
      console.error(err);
    });
}
</script>

<template>
  <div
    class="absolute w-full h-full bg-cover bg-no-repeat flex"
    :class="'theme-' + $page.props.theme ?? 'spitta'"
  >
    <div class="bg-white w-full max-w-8xl m-auto rounded-xl z-20">
      <header
        class="bg-gray-100 flex justify-between items-center px-12 py-8 rounded-tl-xl rounded-tr-xl relative"
      >
        <div class="flex">
          <h2
            class="text-3xl py-3 mr-8"
            :class="{
              'font-semibold': props.type === 'slot',
              'font-light opacity-40': props.type !== 'slot',
            }"
          >
            Terminsuche
          </h2>
        </div>

        <div class="flex relative">
          <HeaderButton class="mr-3" @click="emit('close')" type="close"
            >Schließen</HeaderButton
          >
        </div>
      </header>
      <div class="flex p-12 relative">
        <div class="w-[33%] pr-14">
          <div class="">
            <Label value="Behandler/in" />

            <Select class="w-full mr-4 mb-4" v-model="practitioner_id">
              <option disabled value="" class="">Bitte wählen</option>

              <option
                v-for="u in props.practitioners"
                :key="u.id"
                :value="u.id"
              >
                {{ `${u.firstname} ${u.lastname}` }}
              </option>
            </Select>
          </div>

          <div class="mt-8">
            <Label value="Behandlungsraum" />
            <Select class="w-full mr-4 mb-4" v-model="room_id">
              <option disabled value="" class="">Bitte wählen</option>

              <option v-for="r in props.rooms" :key="r.id" :value="r.id">
                {{ `${r.name}` }}
              </option>
            </Select>
          </div>

          <div class="mt-8">
            <Label value="Mindestdauer" />
            <Select class="w-full mr-4 mb-4" v-model="duration">
              <option disabled value="" class="">Bitte wählen</option>

              <option v-for="n in 10" :key="n" :value="n">
                <!-- {{ n * interval }} Min. -->
                {{ `${n} Einheiten` }} - {{ n * interval }} Min.
              </option>
            </Select>
          </div>

          <div class="mt-8">
            <Label value="Datum" />
            <div class="flex gap-4">
              <Input class="w-full" type="date" v-model="date_start" />
              <Input
                class="w-full"
                type="date"
                :min="date_start"
                @focus="
                  () => {
                    if (!date_end) {
                      date_end = date_start;
                    }
                  }
                "
                v-model="date_end"
              />
            </div>
          </div>

          <div class="mt-8">
            <Button @click="searchSlots" class="w-full">Termine suchen</Button>
          </div>
        </div>

        <div class="w-[67%]">
          <div v-if="freeSlots.length > 0" class="mb-8 text-lg font-light">
            <span class="font-semibold"
              >{{ freeSlots.length }} freie Termine</span
            >
            vom
            {{
              new Date(date_start_to_show).toLocaleDateString("de-DE", {
                year: "numeric",
                month: "2-digit",
                day: "2-digit",
              })
            }}
            bis
            {{
              new Date(date_end_to_show).toLocaleDateString("de-DE", {
                year: "numeric",
                month: "2-digit",
                day: "2-digit",
              })
            }}
          </div>
          <div class="max-h-[530px] overflow-y-scroll">
            <table class="min-w-full">
              <thead class="bg-white border-b">
                <tr class="sticky -top-px border-white border-t bg-white">
                  <th
                    scope="col"
                    class="text-sm font-medium text-gray-900 px-6 py-4 text-left border-x-2"
                  >
                    Behandler/in
                  </th>
                  <th
                    scope="col"
                    class="text-sm font-medium text-gray-900 px-6 py-4 text-left border-x-2"
                  >
                    Behandlungsraum
                  </th>

                  <th
                    scope="col"
                    class="text-sm font-medium text-gray-900 px-6 py-4 text-left border-x-2"
                  >
                    Datum
                  </th>
                  <th
                    scope="col"
                    class="text-sm font-medium text-gray-900 px-6 py-4 text-left border-x-2"
                  >
                    Wochentag
                  </th>
                  <th
                    scope="col"
                    class="text-sm font-medium text-gray-900 px-6 py-4 text-left border-x-2"
                  >
                    Zeit
                  </th>
                </tr>
              </thead>
              <tbody class="">
                <tr
                  v-for="(slot, index) in freeSlots"
                  :key="slot.id"
                  class="border-b cursor-pointer"
                  :class="{
                    'bg-gray-100': index % 2 === 0,
                  }"
                  @click="emit('chooseSlot', slot)"
                >
                  <TD>
                    {{ userName }}
                  </TD>
                  <TD> {{ roomName }} </TD>

                  <TD> {{ `${slot.dateString}` }} </TD>
                  <TD> {{ `${slot.weekday}` }} </TD>
                  <TD> {{ `${slot.startString} - ${slot.endString} Uhr` }} </TD>
                </tr>
              </tbody>
            </table>
            <div class="mt-4" v-if="noSlotsFound">
              Keine freien Termine in diesem Zeitraum gefunden.
            </div>
            <div
              class="sticky bottom-0 h-10 w-full bg-gradient-to-t from-white"
            ></div>
            <div class="h-8 w-full bg-transparent" ref="obsElement"></div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
